import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { camelToSnakeCase } from '../../../utils/caseConversion';
import { parseError } from 'utils/parseTypedErrorMessage';
import { useActualIncidentId } from './commonHooks';

export type ResourceSet = {
  id: UUID;
  resource_set_id: UUID;
  resource_site_id: UUID;
  group_guid: UUID;
  name: string;
  description: string;
  geolocation: any;
  kind: string;
  type: string;
  quantity: number;
  status: string;
  consolidation_target_id: UUID;
  archived: boolean;
  date_time_ordered: string; // As a string in ISO format
  datetime_ordered_offset?: number;
  eta: string; // As a string in ISO format
  eta_timezone_offset?: number;
  incidents: UUID[];
  incident_details?: {
    id: UUID;
    name: string;
  }[]; // Optional detailed info about incidents
  current_status?: string; // From ics_resource_in_incident table
  asset_status?: string;
  asset_status_timestamp?: string;
  source_location?: any;
  source_resource_site_id?: UUID;
  destination_location?: any;
  destination_resource_site_id?: UUID;
  in_request?: boolean;
  point_of_contact?: any;
  aor?: any;
  opcon?: any;
  tacon?: any;
  persons_on_board?: any;
  hull_tail_number?: string;
  unit?: string;
  owning_organization?: string;
  address?: string;
  symbology?: string;
  resource_in_incident_details?: {
    id: string;
    incident_id: string;
    assignees: {
      id: string;
      name: string;
      email: string;
    }[];
  }[];
  notes?: string;
  geometry?: {
    type: string;
    coordinates: [number, number];
  } | null;
  is_external_resource: boolean;
  external_resource_info?: Record<string, any> | null;
  external_resource_info_last_updated?: string | null;
  external_resource_info_out_of_date: boolean;
};

type ResourceSetPagination = {
  name?: string[];
  kind?: string[];
  aor?: string[];
  incident?: string[];
  workAvailability?: string[];
  requestStatus?: string[];
  assetStatus?: string[];
  resultsPerPage?: number;
  pageNumber?: number;
};

export type ResourceSetFilterSelection = {
  name: string[];
  kind: string[];
  aor: string[];
  incident: string[];
  workAvailability: string[];
  requestStatus: string[];
  assetStatus: string[];
  resultsPerPage: number;
  pageNumber: number;
};

export type PaginatedResourceSetsResult = {
  totalItems: number;
  totalPages: number;
  pageNumber: number;
  resultsPerPage: number;
  rows: ResourceSet[];
};

export const keys = {
  base: 'resource_sets',
  get all() {
    return [keys.base];
  },
  list: (
    groupGuid: UUID,
    userGuid: UUID,
    pagination?: ResourceSetPagination
  ) => [keys.base, groupGuid, userGuid, pagination ?? null],
  usage: (resourceSetId: UUID) => [keys.base, 'usage', resourceSetId],
} as const;

export const useResourceSets = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<ResourceSet[]>(
        `${config.apiGateway.ics_resource_sets}/${group_guid}`
      );
      return resp.data;
    },
  });
};

export const usePaginatedResourceSets = (pagination: ResourceSetPagination) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);

  return useQuery({
    queryKey: keys.list(group_guid, user_guid, pagination),
    queryFn: async () => {
      const toSnakeCase = (key: string) =>
        key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

      const searchParams = new URLSearchParams();
      Object.entries(pagination).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const snakeKey = toSnakeCase(key);
        if (Array.isArray(value)) {
          if (value.length === 0) return;
          value.forEach((v) => searchParams.append(snakeKey, v));
        } else {
          searchParams.append(snakeKey, String(value));
        }
      });

      const resp = await axios.get<PaginatedResourceSetsResult>(
        `${config.apiGateway.ics_resource_sets_paginated}/${group_guid}`,
        { params: searchParams }
      );
      return resp.data;
    },
  });
};

// CamelCase version for frontend form data
export type CreateResourceSetFormData = {
  name: string;
  description: string;
  geolocation: any;
  resourceSiteId?: UUID | null;
  kind: string;
  type: string;
  assetStatus?: string;
  quantity: number;
  status: string;
  consolidationTargetId: UUID;
  archived: boolean;
  dateTimeOrdered: string;
  datetimeOrderedOffset?: number;
  eta: string;
  etaTimezoneOffset?: number;
  notes?: string;
  incidents: UUID[];
  members?: UUID[]; // For UI form handling, not sent to backend
  sourceLocation?: any;
  sourceResourceSiteId?: UUID | null;
  destinationLocation?: any;
  destinationResourceSiteId?: UUID | null;
  symbology?: string;
  aor?: string;
  geometry?: {
    type: string;
    coordinates: [number, number];
  } | null;
};

export const useCreateResourceSet = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateResourceSetFormData) => {
      // Convert camelCase form data to snake_case for backend
      // Exclude members field as it's handled separately
      const { members: _members, ...dataForBackend } = data;
      const snakeCaseData = camelToSnakeCase(dataForBackend);
      return await axios.post(`${config.apiGateway.ics_resource_sets}`, {
        ...snakeCaseData,
        group_guid,
        user_guid,
        incident_id: incidentId ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
      toast.success('Resource created successfully');
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to create resource');
    },
  });
};

// CamelCase version for frontend form data
export type EditResourceSetFormData = {
  id?: UUID;
  resourceSetId: UUID;
  resourceSiteId?: UUID | null;
  groupGuid?: UUID;
  name: string;
  description: string;
  geolocation: any;
  kind: string;
  type: string;
  assetStatus?: string;
  quantity: number;
  status: string;
  consolidationTargetId: UUID;
  archived: boolean;
  dateTimeOrdered: string;
  datetimeOrderedOffset?: number;
  eta: string;
  etaTimezoneOffset?: number;
  notes?: string;
  incidents: UUID[];
  members?: UUID[]; // For UI form handling, not sent to backend
  sourceLocation?: any;
  sourceResourceSiteId?: UUID | null;
  destinationLocation?: any;
  destinationResourceSiteId?: UUID | null;
  symbology?: string;
  aor?: string;
  geometry?: {
    type: string;
    coordinates: [number, number];
  } | null;
};

export const useUpdateResourceSet = (resourceSetId: UUID) => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<EditResourceSetFormData>) => {
      // Convert camelCase form data to snake_case for backend
      // Exclude members field as it's handled separately
      const { members: _members, ...dataForBackend } = data;
      const snakeCaseData = camelToSnakeCase(dataForBackend);
      return await axios.put<ResourceSet>(
        `${config.apiGateway.ics_resource_sets}/${resourceSetId}`,
        {
          ...snakeCaseData,
          user_guid,
          group_guid,
          incident_id: incidentId ?? null,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      toast.success('Resource updated successfully');
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to update resource');
    },
  });
};

export const useFetchResourceSetUsage = (resourceSetId: string) => {
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: keys.usage(resourceSetId),
    queryFn: async () => {
      try {
        const resp = await axios.get(
          `${config.apiGateway.ics_resource_sets}/usage/${resourceSetId}`,
          {
            params: {
              group_guid: group_guid,
            },
          }
        );
        return resp.data;
      } catch (error: unknown) {
        toast.error(parseError(error) ?? 'Failed to fetch resource usage');
      }
    },
  });
};

// Define types for the mutation function parameters
type SplitResourceSetParams = {
  resource_set_id: UUID;
  quantity: number;
  name: string;
  resource_site_id: UUID;
};

type JoinResourceSetsParams = {
  source_id: UUID;
  target_id: UUID;
};

export const useSplitResourceSet = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      resource_set_id,
      quantity,
      name,
      resource_site_id,
    }: SplitResourceSetParams) => {
      return await axios.post(
        `${config.apiGateway.ics_resource_sets}/${resource_set_id}/split`,
        {
          quantity,
          name,
          resource_site_id,
          user_guid,
          group_guid,
          incident_id: incidentId ?? null,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to split resource');
    },
  });
};

export const useJoinResourceSets = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({ source_id, target_id }: JoinResourceSetsParams) => {
      return await axios.post(
        `${config.apiGateway.ics_resource_sets}/${target_id}/join/${source_id}`,
        { user_guid, group_guid, incident_id: incidentId ?? null }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to join resources');
    },
  });
};

export const useForceFetchExternalData = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);

  return useMutation({
    mutationFn: async () => {
      return await axios.post(
        `${config.apiGateway.ics_resource_sets}/force_fetch_external_data`,
        { group_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
      toast.success('External data fetch initiated successfully');
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to fetch external data');
    },
  });
};
